import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Icon,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { FaPlay, FaRocket, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { launchPokerWithAutoLogin } from '../../services/autoLoginService';
import { demoConfig } from '../../config/demoConfig';

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { t, ready } = useTranslation();
  const toast = useToast();
  const [isLaunching, setIsLaunching] = useState(false);

  const features = [
    {
      icon: FaRocket,
      text: t('heroSection.features.enterpriseGrade'),
      color: 'white',
    },
    {
      icon: FaShieldAlt,
      text: t('heroSection.features.bankLevel'),
      color: 'white',
    },
    {
      icon: FaUsers,
      text: t('heroSection.features.activeOperators'),
      color: 'white',
    },
  ];

  if (!ready) {
    return null;
  }

  const handleTryDemo = async () => {
    setIsLaunching(true);
    try {
      await launchPokerWithAutoLogin(demoConfig.pokerDesktop);
    } catch (error) {
      toast({
        title: t('heroSection.toast.error'),
        description: t('heroSection.toast.errorDesc'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLaunching(false);
    }
  };

  const handleScheduleDemo = () => {
    navigate('/demo');
  };

  const handleViewProducts = () => {
    navigate('/products');
  };

  const handleContactUs = () => {
    navigate('/contact');
  };

  return (
    <Box
      bgImage="url('/images/background.jpg')"
      bgSize='cover'
      bgPosition='center'
      bgRepeat='no-repeat'
      position='relative'
      color='white'
      py={{ base: 12, md: 16, lg: 20 }}
      minH={{ base: '80vh', md: 'auto' }}
      _before={{
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bg: 'blackAlpha.800',
        zIndex: 1,
      }}
    >
      <Container maxW='7xl' position='relative' zIndex={2}>
        <SimpleGrid
          columns={{ base: 1, lg: 2 }}
          spacing={{ base: 8, md: 10, lg: 12 }}
          alignItems='center'
        >
          {/* Left Content */}
          <VStack
            spacing={{ base: 6, md: 8 }}
            align='start'
            textAlign={{ base: 'start', lg: 'left' }}
          >
            <VStack spacing={4} align={{ base: 'center', lg: 'start' }}>
              <Badge
                bg='primary.500'
                color='white'
                variant='solid'
                px={3}
                py={1}
                fontSize='sm'
                fontWeight='medium'
              >
                {t('heroSection.badge')}
              </Badge>
              <Heading
                size={{ base: 'xl', md: '2xl', lg: '3xl' }}
                lineHeight='1.2'
                textAlign={{ base: 'center', lg: 'left' }}
              >
                {t('heroSection.title')}
                <Text as='span' color='accent.300'>
                  {' '}
                  {t('heroSection.titleHighlight')}
                </Text>
              </Heading>
              <Text fontSize={{ base: 'lg', md: 'xl' }} opacity={0.9} maxW='lg'>
                {t('heroSection.subtitle')}
              </Text>
            </VStack>

            <VStack spacing={4} w='full'>
              <HStack
                spacing={4}
                flexWrap='wrap'
                justify={{ base: 'start', lg: 'flex-start' }}
              >
                <Button
                  size={{ base: 'md', md: 'lg' }}
                  colorScheme='white'
                  color='primary.500'
                  leftIcon={<FaPlay />}
                  onClick={handleTryDemo}
                  isLoading={isLaunching}
                  loadingText={t('heroSection.buttons.launching')}
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                  w='auto'
                  minW={{ base: '100px', sm: '200px' }}
                >
                  {t('heroSection.buttons.tryDemo')}
                </Button>
                <Button
                  size={{ base: 'md', md: 'lg' }}
                  variant='outline'
                  color='white'
                  borderColor='white'
                  onClick={handleViewProducts}
                  _hover={{ bg: 'white', color: 'primary.500' }}
                  w='auto'
                  minW={{ base: '100px', sm: '200px' }}
                >
                  {t('heroSection.buttons.viewProducts')}
                </Button>
              </HStack>
            </VStack>

            {/* Features */}
            <SimpleGrid
              columns={{ base: 1, sm: 2, md: 3 }}
              spacing={{ base: 4, md: 6 }}
              w='full'
            >
              {features.map((feature, index) => (
                <HStack
                  key={index}
                  spacing={3}
                  justify={{ base: 'start', lg: 'flex-start' }}
                >
                  <Icon as={feature.icon} boxSize={5} color={feature.color} />
                  <Text
                    fontSize={{ base: 'xs', md: 'sm' }}
                    fontWeight='medium'
                    textAlign={{ base: 'center', lg: 'left' }}
                  >
                    {feature.text}
                  </Text>
                </HStack>
              ))}
            </SimpleGrid>
          </VStack>

          {/* Right Content - Stats */}
          <VStack spacing={{ base: 6, md: 8 }} align='center' w='full'>
            <Box
              bg='whiteAlpha.200'
              backdropFilter='blur(10px)'
              p={{ base: 6, md: 8 }}
              borderRadius='xl'
              border='1px solid'
              borderColor='whiteAlpha.300'
              w='full'
              maxW={{ base: 'sm', md: 'md' }}
            >
              <VStack spacing={{ base: 4, md: 6 }}>
                <Heading size={{ base: 'md', md: 'lg' }} textAlign='center'>
                  {t('heroSection.stats.title')}
                </Heading>

                <SimpleGrid columns={2} spacing={{ base: 4, md: 6 }} w='full'>
                  <VStack spacing={2}>
                    <Text
                      fontSize={{ base: '2xl', md: '3xl' }}
                      fontWeight='bold'
                      color='accent.300'
                    >
                      {t('heroSection.stats.operators.value')}
                    </Text>
                    <Text
                      fontSize={{ base: 'xs', md: 'sm' }}
                      textAlign='center'
                    >
                      {t('heroSection.stats.operators.label')}
                    </Text>
                  </VStack>

                  <VStack spacing={2}>
                    <Text
                      fontSize={{ base: '2xl', md: '3xl' }}
                      fontWeight='bold'
                      color='accent.300'
                    >
                      {t('heroSection.stats.volume.value')}
                    </Text>
                    <Text
                      fontSize={{ base: 'xs', md: 'sm' }}
                      textAlign='center'
                    >
                      {t('heroSection.stats.volume.label')}
                    </Text>
                  </VStack>

                  <VStack spacing={2}>
                    <Text
                      fontSize={{ base: '2xl', md: '3xl' }}
                      fontWeight='bold'
                      color='accent.300'
                    >
                      {t('heroSection.stats.uptime.value')}
                    </Text>
                    <Text
                      fontSize={{ base: 'xs', md: 'sm' }}
                      textAlign='center'
                    >
                      {t('heroSection.stats.uptime.label')}
                    </Text>
                  </VStack>

                  <VStack spacing={2}>
                    <Text
                      fontSize={{ base: '2xl', md: '3xl' }}
                      fontWeight='bold'
                      color='accent.300'
                    >
                      {t('heroSection.stats.support.value')}
                    </Text>
                    <Text
                      fontSize={{ base: 'xs', md: 'sm' }}
                      textAlign='center'
                    >
                      {t('heroSection.stats.support.label')}
                    </Text>
                  </VStack>
                </SimpleGrid>

                <Button
                  w='full'
                  variant='solid'
                  bg='white'
                  color='primary.500'
                  onClick={handleContactUs}
                  _hover={{ bg: 'gray.100' }}
                >
                  {t('heroSection.buttons.requestCaseStudy')}
                </Button>
              </VStack>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default HeroSection;
